import { publicRequest } from "../../../requestMeth";
import {
  maximetroStart,
  maximetroSuccess,
  maximetroFailure,
} from "../../reducers/data/maximetroRedux";

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const formatDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const tsKey = (row) => row?.timestamp || row?.date_time;

const toArgentinaDayRangeUTC = (startD, endD) => {
  if (!startD) return { startISO: undefined, endISO: undefined };

  const s = new Date(startD);
  const e = endD ? new Date(endD) : new Date(startD);
  const startLocal = new Date(
    s.getFullYear(),
    s.getMonth(),
    s.getDate(),
    0,
    0,
    0,
    0,
  );
  const endLocal = new Date(
    e.getFullYear(),
    e.getMonth(),
    e.getDate(),
    23,
    59,
    59,
    999,
  );

  return { startISO: startLocal.toISOString(), endISO: endLocal.toISOString() };
};

const clampByArgentinaRange = (rows, startISO, endISO) => {
  if (!Array.isArray(rows) || !rows.length || !startISO || !endISO) return rows;

  const minTs = Date.parse(startISO);
  const maxTs = Date.parse(endISO);

  return rows.filter((r) => {
    const raw = tsKey(r);
    const ts = Date.parse(raw);
    return Number.isFinite(ts) && ts >= minTs && ts <= maxTs;
  });
};

const buildEmptyMaximeterPayload = (
  startDate,
  endDate,
  backendMessage = "No hay datos de maxímetro para el período seleccionado.",
) => {
  const startD = startDate instanceof Date ? startDate : new Date(startDate);
  const endD = endDate instanceof Date ? endDate : new Date(endDate);

  const startStr = Number.isNaN(startD.getTime())
    ? null
    : `${startD.getFullYear()}-${pad(startD.getMonth() + 1)}-${pad(startD.getDate())}T00:00:00Z`;

  const endStr = Number.isNaN(endD.getTime())
    ? null
    : `${endD.getFullYear()}-${pad(endD.getMonth() + 1)}-${pad(endD.getDate())}T00:00:00Z`;

  return {
    billing_period: {
      start: startStr,
      end: endStr,
      timezone: "America/Argentina/Buenos_Aires",
    },
    config: null,
    result: {
      overall: {
        raw_max_demand: 0,
        measured_max_demand: 0,
        timestamp_of_max_utc: null,
        interval_energy: 0,
        contracted_power: null,
      },
      by_tou: {},
    },
    quality: {
      expected_intervals: 0,
      received_intervals: 0,
      missing_intervals: 0,
      missing_ratio: 0,
      status: "NO_DATA",
    },
    series: [],
    audit: {
      top_intervals: [],
    },
    meta: {
      version_number: null,
      calculated_at: null,
      calculation_reason: null,
      is_current: false,
      from_cache: false,
      no_data: true,
      message: backendMessage,
    },
  };
};

const shouldReturnEmptyMaximeter = (error) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;

  if (status !== 404) return false;

  return (
    message === "No se encontró configuración activa de maximeter." ||
    message === "No se encontraron datos de energía para el período especificado." ||
    message === "No hay dispositivos activos en la sucursal."
  );
};

const fetchMaximetro = async (
  dispatch,
  token,
  endpoint,
  start,
  end,
  frec,
  mac,
  extra = {},
) => {
  dispatch(maximetroStart());

  try {
    const startD = start instanceof Date ? start : new Date(start);
    const endD = end ? (end instanceof Date ? end : new Date(end)) : null;

    if (Number.isNaN(startD.getTime())) throw new Error("start inválido");

    const hasRange = Boolean(endD);
    const hasFrec =
      frec !== undefined && frec !== null && String(frec).trim() !== "";
    const frecParam = hasFrec ? String(frec).trim() : undefined;
    const startFormat = formatDate(startD);
    const endFormatPlus1 = formatDate(addDays(hasRange ? endD : startD, 1));
    const { startISO, endISO } = toArgentinaDayRangeUTC(
      startD,
      hasRange ? endD : startD,
    );

    let url = `/maximetro/${endpoint}?startDate=${startFormat}&endDate=${endFormatPlus1}&mac=${mac}`;

    if (hasFrec) url += `&frec=${encodeURIComponent(frecParam)}`;

    Object.entries(extra || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        url += `&${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
      }
    });

    const res = await publicRequest.get(url, {
      headers: { token: `Bearer ${token}` },
    });

    let data = res.data;

    const isWrapped =
      data &&
      typeof data === "object" &&
      !Array.isArray(data) &&
      Array.isArray(data.data);

    const rows = isWrapped ? data.data : data;
    const clamped = clampByArgentinaRange(rows, startISO, endISO);
    const finalData = isWrapped ? { ...data, data: clamped } : clamped;

    dispatch(maximetroSuccess(finalData));
    return finalData;
  } catch (error) {
    dispatch(maximetroFailure());
    throw error;
  }
};

export const getPotActiva = (dispatch, token, start, end, frec, mac) =>
  fetchMaximetro(dispatch, token, "potActiva", start, end, frec, mac);

export const getPotReactiva = (dispatch, token, start, end, frec, mac) =>
  fetchMaximetro(dispatch, token, "potReactiva", start, end, frec, mac);

export const getTension = (dispatch, token, start, end, frec, mac) =>
  fetchMaximetro(dispatch, token, "tension", start, end, frec, mac);

export const getCorriente = (dispatch, token, start, end, frec, mac) =>
  fetchMaximetro(dispatch, token, "corriente", start, end, frec, mac);

export const getEActiva = (dispatch, token, start, end, frec, mac) =>
  fetchMaximetro(dispatch, token, "eActiva", start, end, frec, mac);

export const getEReactiva = (dispatch, token, start, end, frec, mac) =>
  fetchMaximetro(dispatch, token, "eReactiva", start, end, frec, mac);

export const getCalculatedParameterMaximetro = (
  dispatch,
  token,
  start,
  end,
  frec,
  mac,
  id,
) =>
  fetchMaximetro(
    dispatch,
    token,
    "calculatedparameter",
    start,
    end,
    frec,
    mac,
    { id },
  );

export const getMaximeter = async (
  dispatch,
  token,
  start,
  end,
  frec,
  branchId,
) => {
  dispatch(maximetroStart());

  try {
    const startD = start instanceof Date ? start : new Date(start);
    const endD = end ? (end instanceof Date ? end : new Date(end)) : null;

    if (Number.isNaN(startD.getTime())) throw new Error("start inválido");
    if (!endD || Number.isNaN(endD.getTime())) throw new Error("end inválido");

    const startFormat = formatDate(startD);
    const endFormat = formatDate(endD);

    let url = `/maximeter?startDate=${startFormat}&endDate=${endFormat}&branchId=${branchId}`;

    if (frec !== undefined && frec !== null && String(frec).trim() !== "") {
      url += `&frec=${encodeURIComponent(String(frec).trim())}`;
    }

    const res = await publicRequest.get(url, {
      headers: { token: `Bearer ${token}` },
    });

    const data = res.data;
    dispatch(maximetroSuccess(data));
    return data;
  } catch (error) {
    if (shouldReturnEmptyMaximeter(error)) {
      const emptyPayload = buildEmptyMaximeterPayload(
        start,
        end,
        error?.response?.data?.message,
      );

      dispatch(maximetroSuccess(emptyPayload));
      return emptyPayload;
    }

    dispatch(maximetroFailure());
    throw error;
  }
};

export const getMaximeterConfigs = async (token) => {
  try {
    const res = await publicRequest.get("/maximeter/configs", {
      headers: { token: `Bearer ${token}` },
    });

    return res.data;
  } catch (error) {
    throw error;
  }
};

export const createMaximeterConfig = async (payload, token) => {
  try {
    const res = await publicRequest.post("/maximeter/configs", payload, {
      headers: { token: `Bearer ${token}` },
    });
    return res;
  } catch (error) {
    throw error;
  }
};

export const updateMaximeterConfig = async (id, payload, token) => {
  try {
    const res = await publicRequest.put(`/maximeter/configs/${id}`, payload, {
      headers: { token: `Bearer ${token}` },
    });
    return res;
  } catch (error) {
    throw error;
  }
};

export const recalculateMaximeter = async (
  token,
  startDate,
  endDate,
  branchId,
) => {
  try {
    const res = await publicRequest.post(
      "/maximeter/recalculate",
      {
        branchId,
        startDate: startDate.toISOString().slice(0, 10),
        endDate: endDate.toISOString().slice(0, 10),
      },
      {
        headers: { token: `Bearer ${token}` },
      },
    );

    return res.data;
  } catch (error) {
    if (shouldReturnEmptyMaximeter(error)) {
      return buildEmptyMaximeterPayload(
        startDate,
        endDate,
        error?.response?.data?.message,
      );
    }

    throw error;
  }
};

export const getMaximeterHistory = async (
  token,
  startDate,
  endDate,
  branchId,
) => {
  const res = await publicRequest.get("/maximeter/history", {
    params: {
      branchId,
      startDate: startDate.toISOString().slice(0, 10),
      endDate: endDate.toISOString().slice(0, 10),
    },
    headers: { token: `Bearer ${token}` },
  });
  return res.data;
};

export const getMaximeterByVersion = async (
  token,
  startDate,
  endDate,
  branchId,
  version,
) => {
  try {
    const res = await publicRequest.get("/maximeter", {
      params: {
        branchId,
        startDate: startDate.toISOString().slice(0, 10),
        endDate: endDate.toISOString().slice(0, 10),
        version,
      },
      headers: { token: `Bearer ${token}` },
    });

    return res.data;
  } catch (error) {
    if (shouldReturnEmptyMaximeter(error)) {
      return buildEmptyMaximeterPayload(
        startDate,
        endDate,
        error?.response?.data?.message,
      );
    }

    throw error;
  }
};